import React, { useState } from 'react';
import { Briefcase, Check, Pencil } from 'lucide-react';
import { colors } from '../../styles/tokens';

/**
 * BaggageEditor — Editor visual de bagagem com toggles
 * 
 * Exibe 3 ícones de malas (crescentes) + texto.
 * Ao clicar, abre toggles para cada tipo de bagagem.
 * Salva os 3 campos de uma vez via onSave.
 * 
 * Props:
 * @param {object} baggage - { personal_item, carry_on, checked_bags }
 * @param {string} baggageText - Texto descritivo da bagagem
 * @param {function} onSave - (updatedBaggage) => Promise
 * @param {boolean} saving
 * @param {boolean} saved
 * @param {boolean} disabled
 */
export default function BaggageEditor({
  baggage = {},
  baggageText,
  onSave,
  saving = false,
  saved = false,
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [local, setLocal] = useState({
    personal_item: baggage.personal_item ?? true,
    carry_on: baggage.carry_on ?? false,
    checked_bags: baggage.checked_bags ?? 0,
  });

  const activeColor = colors.accent;
  const inactiveColor = '#cbd5e1';

  const startEditing = () => {
    if (disabled || saving) return;
    setLocal({
      personal_item: baggage.personal_item ?? true,
      carry_on: baggage.carry_on ?? false,
      checked_bags: baggage.checked_bags ?? 0,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setEditing(false);
    if (onSave) {
      // Monta baggage text automaticamente
      const parts = [];
      if (local.personal_item) parts.push('Item pessoal');
      if (local.carry_on) parts.push('Bagagem de mão');
      if (local.checked_bags > 0) parts.push(`${local.checked_bags}x despacho`);
      const text = parts.length > 0 ? parts.join(' + ') : 'Sem bagagem';

      await onSave({
        personal_item: local.personal_item,
        carry_on: local.carry_on,
        checked_bags: local.checked_bags,
      }, text);
    }
  };

  const handleCancel = () => {
    setEditing(false);
  };

  // ── Modo edição ──
  if (editing) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        backgroundColor: '#f0f4ff',
        border: `1.5px solid ${colors.primary}`,
        borderRadius: '6px',
        padding: '0.75rem',
      }}>
        <ToggleRow
          label="Item pessoal"
          sublabel="Mochila / bolsa"
          checked={local.personal_item}
          onChange={(v) => setLocal({ ...local, personal_item: v })}
          iconSize={14}
        />
        <ToggleRow
          label="Bagagem de mão"
          sublabel="Até 10kg"
          checked={local.carry_on}
          onChange={(v) => setLocal({ ...local, carry_on: v })}
          iconSize={16}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Briefcase size={20} color={local.checked_bags > 0 ? activeColor : inactiveColor} strokeWidth={1.5} />
          <span style={{ fontSize: '0.8rem', color: colors.text.primary, flex: 1 }}>
            Despacho (23kg)
          </span>
          <select
            value={local.checked_bags}
            onChange={(e) => setLocal({ ...local, checked_bags: parseInt(e.target.value) })}
            style={{
              padding: '0.25rem 0.5rem',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              fontSize: '0.8rem',
              backgroundColor: '#fff',
            }}
          >
            <option value={0}>Não</option>
            <option value={1}>1 mala</option>
            <option value={2}>2 malas</option>
            <option value={3}>3 malas</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '0.3rem 0.75rem',
              fontSize: '0.75rem',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              color: colors.text.secondary,
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '0.3rem 0.75rem',
              fontSize: '0.75rem',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: colors.accent,
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Salvar
          </button>
        </div>
      </div>
    );
  }

  // ── Modo exibição ──
  const hasBackpack = baggage.personal_item ?? true;
  const hasCarryOn = baggage.carry_on ?? false;
  const hasChecked = (baggage.checked_bags ?? 0) > 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: disabled ? 'default' : 'pointer',
        padding: '2px 4px',
        margin: '-2px -4px',
        borderRadius: '4px',
        transition: 'background-color 0.15s',
        backgroundColor: saved ? 'rgba(80, 207, 173, 0.15)' : (hovered && !disabled ? 'rgba(9, 7, 119, 0.04)' : 'transparent'),
        borderBottom: disabled ? 'none' : `1px dashed ${hovered ? colors.primary : 'transparent'}`,
      }}
      onClick={startEditing}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={disabled ? undefined : 'Clique para editar bagagem'}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
        <Briefcase size={14} color={hasBackpack ? activeColor : inactiveColor} strokeWidth={1.5} />
        <Briefcase size={16} color={hasCarryOn ? activeColor : inactiveColor} strokeWidth={1.5} />
        <Briefcase size={20} color={hasChecked ? activeColor : inactiveColor} strokeWidth={1.5} />
      </div>

      <span style={{
        fontSize: '0.875rem',
        fontWeight: 500,
        color: saved ? '#059669' : colors.text.primary,
        transition: 'color 0.15s',
      }}>
        {baggageText || 'Não informada'}
      </span>

      {saved && <Check size={12} color="#059669" strokeWidth={2.5} />}
      {hovered && !disabled && !saved && (
        <Pencil size={11} color={colors.text.muted} style={{ opacity: 0.5 }} />
      )}
    </div>
  );
}

/**
 * ToggleRow — Linha de toggle individual
 */
function ToggleRow({ label, sublabel, checked, onChange, iconSize }) {
  const activeColor = colors.accent;
  const inactiveColor = '#cbd5e1';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <Briefcase size={iconSize} color={checked ? activeColor : inactiveColor} strokeWidth={1.5} />
      <span style={{ fontSize: '0.8rem', color: colors.text.primary, flex: 1 }}>
        {label}
        {sublabel && <span style={{ color: colors.text.muted, fontSize: '0.7rem' }}> ({sublabel})</span>}
      </span>
      <button
        onClick={() => onChange(!checked)}
        style={{
          width: '36px',
          height: '20px',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: checked ? activeColor : '#d1d5db',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          padding: 0,
        }}
      >
        <div style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          position: 'absolute',
          top: '2px',
          left: checked ? '18px' : '2px',
          transition: 'left 0.2s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  );
}
